import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API } from '../App';
import { NavBar } from '../components/NavBar';
import { useSound } from '../hooks/useSound';
import {
  Trophy, Building, Bot, ArrowDownUp, Blocks, Search,
  Grid2x2, MountainSnow, CookingPot, MapPin, Swords, Clock,
  Gamepad2, Lock
} from 'lucide-react';
import { CurriculumMapButton } from '../components/CurriculumMap';
import axios from 'axios';

const ICON_MAP = {
  trophy: Trophy, building: Building, bot: Bot,
  'arrow-down-up': ArrowDownUp, puzzle: Blocks, search: Search,
  'grid-2x2': Grid2x2, 'mountain-snow': MountainSnow,
  'chef-hat': CookingPot, map: MapPin, swords: Swords, clock: Clock,
  'gamepad-2': Gamepad2,
};

const ALL_GAMES = [
  { game: 'la_course', name_fr: 'La Course', name_en: 'The Race', icon: 'trophy' },
  { game: 'la_tour', name_fr: 'La Tour', name_en: 'The Tower', icon: 'building' },
  { game: 'robot_balayeur', name_fr: 'Robot Balayeur', name_en: 'Robot Wiper', icon: 'bot' },
  { game: 'tri_express', name_fr: 'Tri Express', name_en: 'Sorting Express', icon: 'arrow-down-up' },
  { game: 'batisseur', name_fr: 'Batisseur', name_en: 'Sentence Builder', icon: 'puzzle' },
  { game: 'detective', name_fr: 'Le Detective', name_en: 'The Detective', icon: 'search' },
  { game: 'memoire', name_fr: 'La Memoire', name_en: 'Memory Match', icon: 'grid-2x2' },
  { game: 'avalanche', name_fr: "L'Avalanche", name_en: 'Word Avalanche', icon: 'mountain-snow' },
  { game: 'le_chef', name_fr: 'Le Chef', name_en: 'The Chef', icon: 'chef-hat' },
  { game: 'carte_du_canada', name_fr: 'Carte du Canada', name_en: 'Map of Canada', icon: 'map' },
  { game: 'duel_conjugaison', name_fr: 'Le Duel', name_en: 'Conjugation Duel', icon: 'swords' },
  { game: 'horloge_folle', name_fr: "L'Horloge Folle", name_en: 'Crazy Clock', icon: 'clock' },
];

// Per-game item labels and minimum playable thresholds
// Rich set games (passages, memory sets) need fewer items because each is a full activity
// Quick MCQ games need more items for a meaningful session
const GAME_ITEM_CONFIG = {
  la_course: { singular: 'question', plural: 'questions', minItems: 5 },
  duel_conjugaison: { singular: 'question', plural: 'questions', minItems: 5 },
  la_tour: { singular: 'question', plural: 'questions', minItems: 5 },
  horloge_folle: { singular: 'question', plural: 'questions', minItems: 5 },
  carte_du_canada: { singular: 'question', plural: 'questions', minItems: 5 },
  detective: { singular: 'case', plural: 'cases', minItems: 4 },
  batisseur: { singular: 'sentence', plural: 'sentences', minItems: 3 },
  avalanche: { singular: 'word set', plural: 'word sets', minItems: 2 },
  tri_express: { singular: 'sorting set', plural: 'sorting sets', minItems: 2 },
  memoire: { singular: 'set', plural: 'sets', minItems: 2 },
  robot_balayeur: { singular: 'passage', plural: 'passages', minItems: 2 },
  le_chef: { singular: 'recipe', plural: 'recipes', minItems: 1 },
};

const MONTH_FR = {
  september: 'Septembre', october: 'Octobre', november: 'Novembre',
  december: 'Decembre', january: 'Janvier', february: 'Fevrier',
  march: 'Mars', april: 'Avril', may: 'Mai', june: 'Juin',
};

const LEVEL_LABELS = {
  'pre-A1': 'Beginner', 'A1': 'Elementary', 'A1-A2': 'Intermediate', 'A2': 'Upper Intermediate',
};

const TOPIC_LABELS = {
  greetings_introductions: 'Greetings & Introductions', numbers_time: 'Numbers & Time',
  classroom_school: 'Classroom & School', family_friends: 'Family & Friends',
  food_cooking: 'Food & Cooking', clothing_colours: 'Clothing & Colours',
  weather_seasons: 'Weather & Seasons', animals_nature: 'Animals & Nature',
  body_health: 'Body & Health', community_places: 'Community & Places',
  sports_hobbies: 'Sports & Hobbies', daily_routines: 'Daily Routines',
  holidays_celebrations: 'Holidays & Celebrations', canadian_culture: 'Canadian Culture',
  grammar_verbs: 'Grammar & Verbs', past_tense_advanced: 'Past Tense & Advanced',
  pronunciation_phonics: 'Pronunciation & Sounds',
};

export function UnifiedGameSelect() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { playSound } = useSound();
  const [games, setGames] = useState([]);

  const mode = searchParams.get('mode') || 'school';
  const grade = searchParams.get('grade');
  const month = searchParams.get('month');
  const level = searchParams.get('level');
  const topic = searchParams.get('topic');
  const difficulty = searchParams.get('difficulty');

  useEffect(() => {
    let url;
    if (mode === 'school' && grade && month) {
      url = `${API}/grades/${grade}/months/${month}/games`;
    } else if (mode === 'level' && level) {
      url = `${API}/levels/${encodeURIComponent(level)}/games${topic ? `?topic=${topic}` : ''}`;
    } else if (mode === 'topic' && topic) {
      url = `${API}/topics/${topic}/games${difficulty ? `?difficulty=${difficulty}` : ''}`;
    }
    if (url) {
      axios.get(url).then(r => setGames(r.data)).catch(console.error);
    }
  }, [mode, grade, month, level, topic, difficulty]);

  // Build breadcrumb items based on mode
  const breadcrumbs = useMemo(() => {
    if (mode === 'school') return [`Grade ${grade}`, MONTH_FR[month] || month];
    if (mode === 'level') {
      const parts = [LEVEL_LABELS[level] || level];
      if (topic) parts.push(TOPIC_LABELS[topic] || topic);
      return parts;
    }
    if (mode === 'topic') {
      const parts = [TOPIC_LABELS[topic] || topic];
      if (difficulty) parts.push(difficulty.charAt(0).toUpperCase() + difficulty.slice(1));
      return parts;
    }
    return [];
  }, [mode, grade, month, level, topic, difficulty]);

  const handleBack = () => {
    if (mode === 'school') navigate(`/school/${grade}`);
    else if (mode === 'level') navigate(`/level/${encodeURIComponent(level)}`);
    else if (mode === 'topic') navigate(`/topic/${topic}`);
    else navigate('/browse');
  };

  const handleGameClick = (gameKey) => {
    playSound('click');
    // Build the setup URL with all current filter params
    const params = new URLSearchParams(searchParams);
    navigate(`/play/${gameKey}/setup?${params}`);
  };

  const availableGames = new Map(games.map(g => [g.game, g]));
  const isFilteredMode = mode === 'level' || mode === 'topic';

  return (
    <div data-testid="game-select-screen" style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      <NavBar items={breadcrumbs} onBack={handleBack} />
      <div style={{ flex: 1, padding: '2rem', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
          <h2 style={{
            fontFamily: 'Fredoka, sans-serif', fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)',
            fontWeight: 700, color: 'var(--text-primary)', margin: 0,
          }}>
            CHOOSE YOUR GAME
          </h2>
          <CurriculumMapButton
            size="small"
            queryParams={{ mode, grade, month, level, topic, difficulty }}
            title={breadcrumbs.join(' — ')}
          />
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
          gap: '1.25rem', maxWidth: 1400, margin: '0 auto',
        }}>
          {ALL_GAMES.map((g, i) => {
            const available = availableGames.has(g.game);
            const info = availableGames.get(g.game);
            const IconComp = ICON_MAP[g.icon] || Gamepad2;
            const itemConfig = GAME_ITEM_CONFIG[g.game];

            // In level/topic modes, hide ALL non-playable games (below threshold OR no items)
            if (isFilteredMode) {
              if (!available || !info || !itemConfig || info.count < itemConfig.minItems) {
                return null;
              }
            }

            const itemLabel = info && itemConfig
              ? (info.count === 1 ? itemConfig.singular : itemConfig.plural)
              : 'questions';

            return (
              <button
                key={g.game}
                data-testid={`game-card-${g.game}`}
                className={`game-card ${available ? 'available' : 'disabled'}`}
                onClick={() => { if (available) handleGameClick(g.game); }}
                disabled={!available}
                style={{ animation: `fade-in 0.3s ease ${i * 0.04}s both` }}
              >
                <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', height: '100%', justifyContent: 'center' }}>
                  {!available && (
                    <Lock size={20} style={{ position: 'absolute', top: 12, right: 12, color: 'var(--text-muted)' }} />
                  )}
                  <IconComp size={48} style={{ color: available ? 'var(--primary)' : 'var(--text-muted)' }} />
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontFamily: 'Fredoka, sans-serif', fontSize: '1.5rem', fontWeight: 700, color: available ? 'var(--text-primary)' : 'var(--text-muted)' }}>
                      {g.name_fr}
                    </div>
                    <div style={{ fontSize: '1.1rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                      {g.name_en}
                    </div>
                    {info && (
                      <div style={{ fontSize: '1rem', color: 'var(--text-muted)', marginTop: 4 }}>
                        {info.count} {itemLabel}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
